import { z } from 'zod';

// 05-remediation.md 5.18 parte 3 -- schemas para las formas reales de request/response del
// dominio `spaces`, verificados contra los DTOs y casos de uso del backend
// (`backend/src/spaces/domain/SpaceDTO.js`, `backend/src/spaces/application/{get-space-stats,
// get-space-activity,list-space-members,update-space,remove-member,create-default-doc}/*`,
// `backend/src/notifications/domain/NotificationDTO.js` para `fetchActivity`, y
// `backend/src/users/domain/UserDTO.js` + `userRouter.js` para `fetchAllUsers`/
// `fetchUserProfile`/`suspendMember`, que llaman al dominio `users`, no `spaces`), no
// inventados desde cómo el frontend los consume hoy.
// `.parse()` en todos lados, `z.looseObject` en los DTOs -- mismo criterio que el resto de
// schemas del repo (ver `client/schemas.js` para el razonamiento completo).

const nullish = (schema) => schema.nullish();
const id = z.union([z.string(), z.number()]);

// ── User (UserDTO) -- usado por fetchAllUsers/fetchUserProfile/suspendMember ───────────────
export const UserSchema = z.looseObject({
    id,
    nick: z.unknown().nullish(),
    email: z.unknown().nullish(),
    role: z.unknown().nullish(),
    description: z.unknown().nullish(),
    location: z.unknown().nullish(),
    religion: z.unknown().nullish(),
    tags: z.unknown().nullish(),
    birth_date: z.unknown().nullish(),
    rfc: z.unknown().nullish(),
    map_avatar: z.unknown().nullish(),
    avatar_frames: z.unknown().nullish(),
    avatar_description: z.unknown().nullish(),
    avatar_char_id: z.unknown().nullish(),
    calendar_personal: z.unknown().nullish(),
    created_at: z.unknown().nullish(),
    map_pos_x: z.unknown().nullish(),
    map_pos_y: z.unknown().nullish(),
    timezone: z.unknown().nullish(),
    spaces: z.unknown().nullish(),
    legacy_role: z.unknown().nullish(),
    welcome: z.unknown().nullish(),
    'link-profile-img': z.unknown().nullish(),
    'link-profile-img-thumb': z.unknown().nullish(),
});

// `GET /api/users` NO envuelve en `{success,...}` -- devuelve el array pelón (o `[]` si
// `context==='calendar'` y quien pide no es admin, ver `ListUsers.execute`).
export const FetchAllUsersResponseSchema = z.array(UserSchema);

// `GetUser`/`UpdateUser` sí envuelven -- mismo sobre para `fetchUserProfile` (GET) y
// `suspendMember` (PUT, que reusa este mismo endpoint genérico con `{welcome: 3}`). `user` trae
// además `language` (de `user_preferences`) y los campos KYC dinámicos (`${field}`/
// `${field}_visibility`) que `UserSchema` no enumera -- `looseObject` los deja pasar.
export const GetUserResponseSchema = z.looseObject({
    success: z.boolean(),
    status: nullish(z.number()),
    user: UserSchema.optional(),
    message: nullish(z.string()),
});

// ── Space stats / activity / members ────────────────────────────────────────────────────────
export const GetSpaceStatsResponseSchema = z.looseObject({
    success: z.boolean(),
    stats: z.looseObject({
        tasks: nullish(z.number()),
        docs: nullish(z.number()),
        storageUsedBytes: nullish(z.number()),
    }).optional(),
});

// NotificationDTO (`backend/src/notifications/domain/NotificationDTO.js`) -- no es un dominio
// propio de `spaces`, pero `GetSpaceActivity` es el único caso de uso de este `api.js` que lo
// devuelve, así que el schema vive acá en vez de crear un `notifications/schemas.js` para un
// solo consumidor.
export const NotificationSchema = z.looseObject({
    id,
    type: nullish(z.string()),
    metadata: z.unknown().nullish(),
    created_at: nullish(z.string()),
    user_actor: nullish(id),
    link: nullish(z.string()),
    space_id: nullish(id),
    scope: nullish(z.string()),
    read: nullish(z.boolean()),
    type_label: nullish(z.string()),
    type_icon: nullish(z.string()),
    actor_avatar: nullish(z.string()),
    actor_name: nullish(z.string()),
    space_icon: nullish(z.string()),
    space_name: nullish(z.string()),
    users: z.unknown().nullish(),
});

export const GetSpaceActivityResponseSchema = z.looseObject({
    success: z.boolean(),
    notifications: z.array(NotificationSchema).optional().default([]),
});

// `ListSpaceMembers` devuelve una forma sanitizada distinta según sea admin o no (ver comentario
// en el propio caso de uso) -- para no-admins solo `{id, name, image}` (+ `permissions`/`role`
// si es uno mismo); para admins, la fila de miembro completa. `looseObject` cubre ambos casos
// sin tener que replicar el `UserDTO` completo acá.
export const SpaceMemberSchema = z.looseObject({
    id,
    name: nullish(z.string()),
    image: nullish(z.string()),
    permissions: z.unknown().optional(),
    role: z.unknown().optional(),
});

export const ListSpaceMembersResponseSchema = z.looseObject({
    success: z.boolean(),
    members: z.array(SpaceMemberSchema).optional().default([]),
});

export const UpdateSpaceResponseSchema = z.looseObject({
    success: z.boolean(),
    error: nullish(z.string()),
});

export const RemoveMemberResponseSchema = z.looseObject({
    success: z.boolean(),
});

export const CreateSpaceDefaultDocResponseSchema = z.looseObject({
    success: z.boolean(),
    docId: nullish(id),
});
